<template>
  <!-- Proxies page for ProProxyUI servers: one port = HTTP + SOCKS5, bound to one modem -->
  <div class="pu">
    <div class="pu-table">
      <div class="pu-row pu-row--head">
        <span></span><span>Server</span><span>Modem</span><span>Port</span><span>Login / whitelist</span><span>Public IP</span><span>Status</span><span>VPN</span><span>Sold to</span><span>OS</span><span>Today</span><span></span>
      </div>
      <div v-for="p in rows" :key="p.uuid" class="pu-row" :class="{ 'is-off': p.active === 'inactive' }">
        <span><input type="checkbox" class="form-check-input shadow-none" :checked="selected.includes(p.uuid)" @change="toggle(p.uuid)" /></span>
        <span class="pu-two"><b>{{ p.modem }}</b><small v-if="p.active === 'inactive'">not for sale</small></span>
        <span class="pu-two"><span>{{ modemLabel(p) }}</span><small>…{{ String(p.imei).slice(-4) }}</small>
          <small v-if="p.modem_match === 'unmatched'" class="pu-imei-bad">IMEI not found on server{{ p.imei_valid === false ? ' · IMEI looks mistyped' : '' }}</small></span>
        <span class="pu-two"><span class="pu-mono">{{ p.http_port }}<span class="pu-ipm" :class="'is-' + (p.ip_mode || 'ipv4')">{{ ipModeLabel(p.ip_mode) }}</span></span><small v-if="p.sock_port && p.sock_port !== p.http_port">SOCKS5 {{ p.sock_port }}</small></span>
        <span class="pu-two"><span class="pu-mono">{{ p.auth_user || '—' }}</span><small v-if="p.auth_ips">{{ p.auth_ips }}</small></span>
        <span class="pu-two"><span class="pu-mono">{{ p.public_ip || '—' }}</span><small v-if="p.ipv6_address" class="pu-mono" :title="p.ipv6_address">{{ p.ipv6_address }}</small></span>
        <span><span class="pu-pill" :class="p.status === 'CONNECTED' ? 'is-on' : 'is-bad'">{{ p.status === 'CONNECTED' ? 'Online' : 'Offline' }}</span></span>
        <span class="pu-two pu-vpn">
          <template v-if="vpnStates[p.uuid]">
            <span><i class="pu-dot" :class="{ 'is-on': vpnStates[p.uuid].wg_connected }"></i>WireGuard<small v-if="vpnStates[p.uuid].wg_connected && vpnStates[p.uuid].wg_seen"> · {{ ago(vpnStates[p.uuid].wg_seen) }}</small>
              <button type="button" class="pu-dl" title="Download the WireGuard file" @click="downloadVpn(p, 'wg')"><v-icon icon="mdi-download" size="13"></v-icon></button></span>
            <span><i class="pu-dot" :class="{ 'is-on': vpnStates[p.uuid].ovpn_connected }"></i>OpenVPN<small v-if="vpnStates[p.uuid].ovpn_sessions"> · {{ vpnStates[p.uuid].ovpn_sessions }} session{{ vpnStates[p.uuid].ovpn_sessions === 1 ? '' : 's' }}</small>
              <button type="button" class="pu-dl" title="Download the OpenVPN file" @click="downloadVpn(p, 'ovpn')"><v-icon icon="mdi-download" size="13"></v-icon></button></span>
          </template>
          <small v-else>—</small>
        </span>
        <span>{{ p.usage_status === 'AVAILABLE' ? 'Free' : (p.customer_name || 'Sold') }}</span>
        <span>{{ osLabel(p.os_profile) }}</span>
        <span class="pu-mono">{{ p.usage_today_mb }} MB</span>
        <span class="text-right">
          <v-menu location="bottom end">
            <template v-slot:activator="{ props }"><v-btn v-bind="props" density="compact" size="small" class="bg-dark text-light">Actions</v-btn></template>
            <v-list density="compact">
              <v-list-item @click="openEdit(p)">Edit</v-list-item>
              <v-list-item @click="modemAction(p, 'rotate')">Rotate IP</v-list-item>
              <v-list-item @click="modemAction(p, 'reboot')">Reboot modem</v-list-item>
              <v-list-item @click="modemAction(p, 'usbreset')">USB reset</v-list-item>
              <v-list-item @click="toggleActive(p)">{{ p.active === 'inactive' ? 'Put back on sale' : 'Take off sale' }}</v-list-item>
            </v-list>
          </v-menu>
        </span>
      </div>
      <p v-if="!rows.length" class="pu-empty">No ProProxyUI ports{{ selectedServerName && selectedServerName !== 'All' ? ' on this server' : '' }} yet.</p>
    </div>

    <v-dialog v-model="dialog" max-width="560">
      <div class="pu-dlg">
        <div class="pu-dlg__head"><span>{{ form.uuid ? `Edit port ${form.listen_port}` : `Add port · ${selectedServerName}` }}</span><button type="button" aria-label="Close" @click="dialog = false">&times;</button></div>
        <div class="pu-dlg__body">
          <p v-if="boxError" class="pu-err">{{ boxError }}</p>
          <div><label>Modem</label>
            <v-select v-model="form.imei" :items="modemItems" item-title="label" item-value="imei" variant="outlined" density="compact" hide-details rounded="0" :loading="boxLoading"></v-select></div>
          <p v-if="sharedWarning" class="pu-warn">This modem already has {{ sharedWarning.n }} port{{ sharedWarning.n === 1 ? '' : 's' }}{{ sharedWarning.other ? ` (${sharedWarning.other} from another dashboard on this box)` : '' }}. Customers on the same modem share its IP and every rotation.</p>
          <div class="grid grid-cols-2 gap-3">
            <div><label>Port</label><v-text-field v-model.number="form.listen_port" type="number" variant="outlined" density="compact" hide-details rounded="0" :disabled="!!form.uuid" :placeholder="nextPort ? String(nextPort) : 'next free'"></v-text-field></div>
            <div><label>Login (user:password)</label><v-text-field v-model="form.auth_user" variant="outlined" density="compact" hide-details rounded="0" placeholder="random" class="pu-mono-input"></v-text-field></div>
          </div>
          <div><label>IP whitelist</label><v-text-field v-model="form.auth_ips" variant="outlined" density="compact" hide-details rounded="0" placeholder="none"></v-text-field></div>
          <div><label>IP version</label><v-select v-model="form.ip_mode" :items="IP_MODES" item-title="label" item-value="value" variant="outlined" density="compact" hide-details rounded="0"></v-select>
            <small class="pu-note">Sold under IPv4 or IPv6 on Buy Proxy (Dual is sold as IPv6). IPv6 only fails on a modem without IPv6.</small></div>
          <div class="grid grid-cols-3 gap-3">
            <div><label>OS fingerprint</label><v-select v-model="form.os_profile" :items="OS_ITEMS" item-title="label" item-value="value" variant="outlined" density="compact" hide-details rounded="0"></v-select></div>
            <div><label>Speed limit (Mbps)</label><v-text-field v-model.number="form.limit_mbps" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
            <div><label>Daily data (GB)</label><v-text-field v-model.number="form.daily_gb" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
          </div>
          <div class="grid grid-cols-3 gap-3">
            <div><label>Max connections</label><v-text-field v-model.number="form.max_conns" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = none"></v-text-field></div>
            <div><label>Auto-rotate (min)</label><v-text-field v-model.number="form.auto_rotate_min" type="number" min="0" variant="outlined" density="compact" hide-details rounded="0" placeholder="0 = off"></v-text-field></div>
            <div><label>MTU seen by sites</label><v-select v-model="form.mtu" :items="mtuItems" item-title="name" item-value="value" variant="outlined" density="compact" hide-details rounded="0"></v-select>
              <small class="pu-note">{{ carrierMtu ? `Carrier max ${carrierMtu}` : 'Carrier MTU not measured yet' }}</small></div>
          </div>
        </div>
        <div class="pu-dlg__foot">
          <button type="button" class="pu-btn" @click="dialog = false">Cancel</button>
          <button type="button" class="pu-btn pu-btn--pri" :disabled="busy || !form.imei" @click="save">{{ busy ? 'Saving…' : form.uuid ? 'Save' : 'Create port' }}</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, defineProps, defineExpose } from 'vue';
import { storeToRefs } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAdminProxyStore, useAlertStore } from '@/stores';

const props = defineProps({ selectedServerName: String, selectedSaleStatus: String, selectedConnStatus: String });
const API = `${process.env.VUE_APP_VUE_API_URL}`;
const store = useAdminProxyStore();
const { ppuiProxies } = storeToRefs(store);
const alerts = useAlertStore();
const OS_ITEMS = [
  { value: '', label: 'Native' }, { value: 'windows', label: 'Windows' }, { value: 'windows-old', label: 'Windows (old)' }, { value: 'macos', label: 'macOS' },
  { value: 'ios', label: 'iOS' }, { value: 'android', label: 'Android' }, { value: 'android-old', label: 'Android (old)' }, { value: 'linux', label: 'Linux' }, { value: 'ubuntu24', label: 'Ubuntu 24' },
];
const osLabel = (v) => (OS_ITEMS.find((o) => o.value === (v || '')) || { label: v }).label;
// the port's IP version on the box (ipMode): IPv4, IPv6 only, or both with IPv6 first
const IP_MODES = [{ value: 'ipv4', label: 'IPv4' }, { value: 'ipv6', label: 'IPv6 only' }, { value: 'dual', label: 'Dual (IPv6 first, IPv4 fallback)' }];
const ipModeLabel = (v) => (v === 'ipv6' ? 'IPv6' : v === 'dual' ? 'Dual' : 'IPv4');

const rows = computed(() => (ppuiProxies.value || []).filter((p) =>
  (!props.selectedSaleStatus || props.selectedSaleStatus === 'All' || p.usage_status === props.selectedSaleStatus)
  && (!props.selectedConnStatus || props.selectedConnStatus === 'All' || (props.selectedConnStatus === 'PAUSED' ? p.active === 'inactive' : p.status === props.selectedConnStatus))));

const selected = ref([]);
const toggle = (uuid) => { selected.value = selected.value.includes(uuid) ? selected.value.filter((u) => u !== uuid) : [...selected.value, uuid]; };
const reload = () => store.loadProxies(props.selectedServerName || 'All');

// modem names from the last box read (Add / Edit dialog), falling back to "Modem"
const boxModems = ref([]);
const modemLabel = (p) => (boxModems.value.find((m) => String(m.imei) === String(p.imei)) || {}).nickname || 'Modem';

const dialog = ref(false);
const busy = ref(false);
const boxLoading = ref(false);
const boxError = ref('');
const nextPort = ref(null);
const blank = () => ({ uuid: null, imei: null, listen_port: null, auth_user: '', auth_ips: '', os_profile: '', limit_mbps: 0, daily_gb: 0, max_conns: 0, auto_rotate_min: 0, mtu: 0, ip_mode: 'ipv4', modem: null });
const form = ref(blank());
// ports per modem as the box counts them (ours and other dashboards'), modems without one first
const boxCount = (m) => (m.box_ports !== undefined ? m.box_ports : m.ports) || 0;
const modemItems = computed(() => [...boxModems.value]
  .sort((a, b) => (boxCount(a) - boxCount(b)) || String(a.nickname).localeCompare(String(b.nickname)))
  .map((m) => {
    const n = boxCount(m);
    const used = n ? ` · ${n} port${n === 1 ? '' : 's'}${m.other_ports ? ` (${m.other_ports} from another dashboard)` : ''}` : '';
    return { imei: m.imei, label: `${m.nickname} · ${m.state}${m.public_ip ? ' · ' + m.public_ip : ''}${used}` };
  }));
// MTU: match the fingerprint, or a box preset up to the chosen modem's carrier MTU (the box refuses more)
const mtuPresets = ref([]);
const carrierMtu = computed(() => Number((boxModems.value.find((m) => String(m.imei) === String(form.value.imei)) || {}).carrier_mtu) || 0);
const mtuItems = computed(() => {
  const list = [{ value: 0, name: 'Match fingerprint (default)' }, ...mtuPresets.value.filter((p) => carrierMtu.value && p.value <= carrierMtu.value)];
  const cur = Number(form.value.mtu) || 0;
  if (cur && !list.some((p) => p.value === cur)) list.push({ value: cur, name: `${cur} (above this modem's carrier MTU)` });
  return list;
});
const sharedWarning = computed(() => {
  const m = boxModems.value.find((x) => String(x.imei) === String(form.value.imei));
  if (!m) return null;
  const n = boxCount(m) - (form.value.uuid && String(form.value.originalImei) === String(m.imei) ? 1 : 0);
  return n > 0 ? { n, other: m.other_ports || 0 } : null;
});
const loadBox = async (serverName) => {
  boxLoading.value = true; boxError.value = '';
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/box`, { modem_name: serverName });
    boxModems.value = r.modems || []; nextPort.value = r.next_port; mtuPresets.value = r.mtu_presets || [];
  } catch (e) { boxError.value = typeof e === 'string' ? e : 'The box did not answer'; boxModems.value = []; }
  boxLoading.value = false;
};
// modem names for the table when one server is shown
watch(() => props.selectedServerName, (name) => { if (name && name !== 'All' && !dialog.value) loadBox(name); }, { immediate: true });
const openAdd = async () => {
  if (!props.selectedServerName || props.selectedServerName === 'All') { alerts.error('Choose a ProProxyUI server first'); return; }
  form.value = blank(); dialog.value = true;
  await loadBox(props.selectedServerName);
  const free = boxModems.value.find((m) => !boxCount(m) && m.state === 'online') || boxModems.value.find((m) => !boxCount(m)) || boxModems.value[0];
  if (free) form.value.imei = free.imei;
};
const openEdit = async (p) => {
  form.value = { uuid: p.uuid, imei: p.imei, originalImei: p.imei, listen_port: p.http_port, auth_user: p.auth_user || '', auth_ips: p.auth_ips || '', os_profile: p.os_profile || '',
    limit_mbps: p.limit_mbps || 0, daily_gb: p.daily_gb || 0, max_conns: p.max_conns || 0, auto_rotate_min: p.auto_rotate_min || 0, mtu: p.mtu || 0, ip_mode: p.ip_mode || 'ipv4', modem: p.modem };
  dialog.value = true;
  await loadBox(p.modem);
};
const save = async () => {
  const f = form.value;
  const body = { auth_user: String(f.auth_user || '').trim(), auth_ips: f.auth_ips, os_profile: f.os_profile, limit_mbps: f.limit_mbps || 0, daily_gb: f.daily_gb || 0, max_conns: f.max_conns || 0, auto_rotate_min: f.auto_rotate_min || 0, mtu: f.mtu || 0, imei: f.imei, ip_mode: f.ip_mode || 'ipv4' };
  busy.value = true;
  try {
    if (f.uuid) {
      await fetchWrapper.post(`${API}/admin/proproxyui/update-port`, { uuid: f.uuid, ...body });
      alerts.success('Port saved');
    } else {
      const r = await fetchWrapper.post(`${API}/admin/proproxyui/create-port`, { modem_name: props.selectedServerName, listen_port: f.listen_port || undefined, ...body, auth_user: body.auth_user || undefined });
      alerts.success(`Port ${r.listen_port} created`);
    }
    dialog.value = false;
    await reload();
  } catch (e) { alerts.error(e); }
  busy.value = false;
};
const modemAction = async (p, action) => {
  const what = { rotate: 'Rotate the IP of', reboot: 'Reboot', usbreset: 'USB reset' }[action];
  if (!confirm(`${what} the modem behind port ${p.http_port}? Every port on that modem drops for a moment.`)) return;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/modem-action`, { uuid: p.uuid, action });
    alerts.success(action === 'rotate' && r.result ? `New IP ${r.result.newIp}${r.result.changed === false ? ' (the carrier gave the same IP)' : ''}` : 'Done');
    await reload();
  } catch (e) { alerts.error(e); }
};
const toggleActive = async (p) => {
  try { await fetchWrapper.post(`${API}/admin/proproxyui/toggle-active`, { uuid: p.uuid }); await reload(); } catch (e) { alerts.error(e); }
};
const deleteProxies = async () => {
  if (!selected.value.length) { alerts.error('Tick the ports to delete'); return; }
  if (!confirm(`Delete ${selected.value.length} port(s) from the box? Sold ports are skipped.`)) return;
  try {
    const r = await fetchWrapper.post(`${API}/admin/proproxyui/delete-ports`, { uuids: selected.value });
    alerts.success(r.message === 'success' ? 'Deleted' : r.message);
    selected.value = [];
    await reload();
  } catch (e) { alerts.error(e); }
};
// live VPN state (the box keeps it in memory only), every 30 s while this page is open
const vpnStates = ref({});
const loadVpn = async () => {
  if (document.hidden) return;
  try { const r = await fetchWrapper.post(`${API}/admin/proproxyui/vpn-status`, { modem_name: props.selectedServerName || 'All' }); vpnStates.value = r.states || {}; } catch (e) { /* the column shows a dash until the next try */ }
};
let vpnTimer = null;
onMounted(() => { loadVpn(); vpnTimer = setInterval(loadVpn, 30000); });
onBeforeUnmount(() => clearInterval(vpnTimer));
watch(() => props.selectedServerName, loadVpn);
const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return '';
  return s < 60 ? `${s} s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`;
};
// the port's WireGuard / OpenVPN file (a sold port: its customer's)
const downloadVpn = async (p, type) => {
  try {
    const f = await fetchWrapper.post(`${API}/admin/proproxyui/vpn-file`, { port_uuid: p.uuid, type });
    if (!f || !f.content) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([f.content], { type: 'application/octet-stream' }));
    a.download = f.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  } catch (e) { alerts.error(e); }
};
defineExpose({ openAdd, deleteProxies });
</script>

<style scoped>
.pu-table { border: 1px solid #dee2e6; background: #ffffff; color: #000000; overflow-x: auto; }
.pu-row { display: grid; grid-template-columns: 28px minmax(110px, 1.1fr) minmax(110px, 1.1fr) 100px minmax(150px, 1.4fr) 140px 80px 200px minmax(90px, 1fr) 90px 90px 90px; gap: 10px; align-items: center; padding: 8px 12px; border-bottom: 1px solid #dee2e6; min-width: 1330px; font-size: 13px; }
.pu-row:last-child { border-bottom: 0; }
.pu-row--head { color: #6b7280; font-size: 12px; }
.pu-row.is-off { opacity: 0.6; }
.pu-two { display: flex; flex-direction: column; min-width: 0; }
.pu-two small { color: #6b7280; font-size: 11.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pu-mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; overflow-wrap: anywhere; }
.pu-pill { display: inline-block; padding: 1px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 600; }
.pu-pill.is-on { background: #dcfce7; color: #166534; }
.pu-pill.is-bad { background: #fef3c7; color: #92400e; }
.pu-ipm { display: inline-block; margin-left: 5px; padding: 0 6px; border-radius: 999px; font-family: inherit; font-size: 10.5px; font-weight: 600; line-height: 15px; vertical-align: 1px; background: #e5e7eb; color: #374151; }
.pu-ipm.is-ipv6, .pu-ipm.is-dual { background: #dbeafe; color: #1e40af; }
.pu-vpn { font-size: 12px; white-space: nowrap; }
.pu-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px; background: #ef4444; }
.pu-dot.is-on { background: #22c55e; }
.pu-dl { margin-left: 4px; padding: 0 3px; border: 1px solid #22c55e; border-radius: 5px; color: #16a34a; background: transparent; line-height: 14px; cursor: pointer; }
.pu-dl:hover { background: rgba(34, 197, 94, 0.12); }
.pu-empty { padding: 16px; color: #6b7280; margin: 0; }
.pu-dlg { background: #ffffff; color: #000000; border: 1px solid #dee2e6; border-radius: 12px; }
.pu-dlg__head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #dee2e6; font-size: 16px; }
.pu-dlg__head button { font-size: 22px; line-height: 1; color: inherit; background: transparent; border: 0; cursor: pointer; }
.pu-dlg__body { display: flex; flex-direction: column; gap: 12px; padding: 14px 16px; }
.pu-dlg__body label { display: block; margin-bottom: 4px; color: #6b7280; font-size: 12px; }
.pu-dlg__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid #dee2e6; }
.pu-note { display: block; margin-top: 2px; font-size: 11.5px; color: #6b7280; }
.pu-imei-bad { color: #ef4444 !important; font-weight: 500; }
.pu-warn, .pu-err { margin: 0; padding: 6px 10px; border-radius: 8px; background: #fef3c7; color: #92400e; font-size: 12.5px; }
.pu-btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 14px; border: 1px solid #adb5bd; border-radius: 6px; font-size: 13px; color: inherit; background: transparent; cursor: pointer; }
.pu-btn:disabled { opacity: 0.5; cursor: default; }
.pu-btn--pri { background: #0d6efd; border-color: #0d6efd; color: #ffffff; }
.pu-mono-input :deep(input) { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12.5px; }
</style>

<style>
/* dark mode: body.dark (unscoped on purpose: a scoped :global(.dark) rule compiles to .dark alone) */
body.dark .pu-table, body.dark .pu-dlg { background: #34334a; color: #ffffff; border-color: #45445e; }
body.dark .pu-row, body.dark .pu-dlg__head, body.dark .pu-dlg__foot { border-color: #45445e; }
body.dark .pu-row--head, body.dark .pu-two small, body.dark .pu-empty, body.dark .pu-dlg__body label, body.dark .pu-note { color: #bcbbc7; }
body.dark .pu-btn { border-color: #45445e; }
body.dark .pu-ipm { background: #45445e; color: #bcbbc7; }
body.dark .pu-ipm.is-ipv6, body.dark .pu-ipm.is-dual { background: rgba(96, 165, 250, 0.2); color: #93c5fd; }
body.dark .pu-dl { color: #4ade80; }
body.dark .pu-vpn small { color: #bcbbc7; }
</style>
